import EmptyState from "@/components/EmptyState";
import NotificationCircle from "@/components/NotificationCircle";
import PhotosCarousel from "@/components/profile/PhotosCarousel";
import { ProfileViewDetails } from "@/components/profile/ProfileViewDetails";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PrivatePhotoStatus } from "@/generated-api";
import { useToast } from "@/hooks/use-toast";
import {
  useBlockUser,
  useGetProfileView,
  useReportUser,
  useSharePrivatePhotos,
  useUnsharePrivatePhotos,
} from "@/tanstack-hooks";
import {
  BanIcon,
  CheckCircle2Icon,
  ChevronLeftIcon,
  CircleAlertIcon,
  CloudOffIcon,
  EyeOffIcon,
  ImageOffIcon,
  ImageUpIcon,
  MoreHorizontalIcon,
} from "lucide-react";
import { z } from "zod";
import { useEffect, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import SpinnerSmall from "@/components/SpinnerSmall";
import { useTranslation } from "@/hooks/useTranslation";
import ProfileSkeletonLoader from "@/components/profile/ProfileSkeletonLoader";
import { useStandaloneMode } from "@/hooks/useStandaloneMode";
import { isIOS } from "react-device-detect";
import { useHaptics } from "@/hooks/useHaptics";

interface LocationState {
  from?: string;
}

const reportFormSchema = z.object({
  reportReason: z
    .string()
    .min(7, "Minimum 7 characters")
    .max(100, "Maximum 100 characters"),
});

type ReportFormSchema = z.infer<typeof reportFormSchema>;

export default function ProfilePage() {
  const { userId } = useParams();
  const { toast } = useToast();
  const { simpleTrigger } = useHaptics();
  const { data, isLoading, error } = useGetProfileView(userId!);
  const navigate = useNavigate();
  const location = useLocation();
  const blockUserMutation = useBlockUser();
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const reportUserMutation = useReportUser();
  const sharePrivatePhotosMutation = useSharePrivatePhotos();
  const unsharePrivatePhotosMutation = useUnsharePrivatePhotos();
  const isStandalone = useStandaloneMode();
  const t = useTranslation();

  const reportForm = useForm<ReportFormSchema>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      reportReason: "",
    },
  });

  useEffect(() => {
    // Desktop full-page only. On mobile the profile renders inside the
    // overlay sheet, which is its own scroll container; the window holds the
    // underlying list's scroll position and must not be touched.
    if (window.matchMedia("(min-width: 768px)").matches) {
      window.scrollTo(0, 0);
    }
  }, []);

  const handleBackClick = () => {
    const state = location.state as LocationState;
    if (state && state.from) {
      navigate(state.from);
    } else {
      navigate(-1);
    }
  };

  const handleBlockUser = () => {
    blockUserMutation.mutate(userId!, {
      onSuccess: () => {
        navigate(-1);
      },
      onError: (error) => {
        console.error("Error blocking user", error);
        toast({
          variant: "destructive",
          title: t.PROFILE_VIEW.ERRORS.BLOCK,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      },
    });
  };

  function handleReportUser(values: ReportFormSchema) {
    reportUserMutation.mutate(
      { user_to_report: userId!, report_reason: values.reportReason },
      {
        onSuccess: () => {
          setReportModalOpen(false);
          reportForm.reset();
          toast({
            variant: "default",
            // @ts-expect-error Type 'Element' is not assignable to type
            title: (
              <div className="flex items-center gap-2">
                <CheckCircle2Icon className="h-4 w-4" />
                <span>{t.PROFILE_VIEW.LABELS.REPORT_SUCCESS}</span>
              </div>
            ),
            className: "dark rounded-2xl",
          });
        },
        onError: (error) => {
          console.error("Error reporting user", error);
          toast({
            variant: "destructive",
            title: t.PROFILE_VIEW.ERRORS.REPORT,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      },
    );
  }

  const handleSharePrivatePhotos = () => {
    data && (data.my_photos_share_status = PrivatePhotoStatus.SHARED);
    simpleTrigger();
    sharePrivatePhotosMutation.mutate(userId!, {
      onSuccess: () => {
        toast({
          variant: "default",
          // @ts-expect-error Type 'Element' is not assignable to type
          title: (
            <div className="flex items-center gap-2">
              <CheckCircle2Icon className="h-4 w-4" />
              <span>{t.PHOTO_SHARING.LABELS.SHARE_SUCCESS}</span>
            </div>
          ),
          className: "dark rounded-2xl",
          duration: 1500,
        });
      },
      onError: (error) => {
        data && (data.my_photos_share_status = PrivatePhotoStatus.HIDDEN);
        console.error("Error sharing photos", error);
        toast({
          variant: "destructive",
          title: t.PHOTO_SHARING.ERRORS.SHARE,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      },
    });
  };

  const handleUnsharePrivatePhotos = () => {
    data && (data.my_photos_share_status = PrivatePhotoStatus.HIDDEN);
    simpleTrigger();
    unsharePrivatePhotosMutation.mutate(userId!, {
      onSuccess: () => {
        toast({
          variant: "default",
          // @ts-expect-error Type 'Element' is not assignable to type
          title: (
            <div className="flex items-center gap-2">
              <EyeOffIcon className="h-4 w-4" />
              <span>{t.PHOTO_SHARING.LABELS.UNSHARE_SUCCESS}</span>
            </div>
          ),
          className: "dark rounded-2xl",
          duration: 1500,
        });
      },
      onError: (error) => {
        data && (data.my_photos_share_status = PrivatePhotoStatus.SHARED);
        console.error("Error unsharing photos", error);
        toast({
          variant: "destructive",
          title: t.PHOTO_SHARING.ERRORS.UNSHARE,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      },
    });
  };

  if (isLoading) {
    return <ProfileSkeletonLoader />;
  }
  if (error || !data) {
    return (
      <EmptyState
        icon={CloudOffIcon}
        title={t.PROFILE_VIEW.ERRORS.LOADING}
        message={t.PROFILE_VIEW.ERRORS.LOADING_DESCRIPTION}
      />
    );
  }

  return (
    <>
      <div
        className={`flex flex-col w-full max-w-5xl mx-auto ${
          isStandalone && isIOS ? "pb-20" : "pb-16"
        }`}
      >
        <div className="flex flex-row justify-between items-center mb-2 md:mb-4 pl-1">
          <Button variant="default" onClick={handleBackClick}>
            <ChevronLeftIcon className="mr-2 h-4 w-4" />
            {t.SHARED.LABELS.BACK_BUTTON}
          </Button>

          <div className="flex flex-row items-center gap-2">
            {data.my_photos_share_status === PrivatePhotoStatus.HIDDEN ||
            data.my_photos_share_status === PrivatePhotoStatus.REQUESTED ? (
              <>
                <Button
                  variant="default"
                  onClick={handleSharePrivatePhotos}
                  className="px-3 relative"
                >
                  <ImageUpIcon className="h-6 w-6" strokeWidth={1.5} />
                  {data.my_photos_share_status ==
                    PrivatePhotoStatus.REQUESTED && (
                    <NotificationCircle className="m-0 p-0 right-1 top-1 border-none w-[0.50rem] h-[0.50rem]" />
                  )}
                </Button>
              </>
            ) : data.my_photos_share_status === PrivatePhotoStatus.SHARED ? (
              <Button
                variant="default"
                onClick={handleUnsharePrivatePhotos}
                className="px-3"
              >
                <ImageOffIcon className="h-6 w-6" strokeWidth={1.5} />
              </Button>
            ) : null}

            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="default" className="px-3">
                  <MoreHorizontalIcon className="h-6 w-6" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="dark rounded-2xl">
                <DropdownMenuItem
                  onClick={handleBlockUser}
                  className="gap-5 py-3 rounded-xl hover:cursor-pointer"
                  disabled={blockUserMutation.isPending}
                >
                  <BanIcon size={16} />
                  <span>{t.PROFILE_VIEW.LABELS.BLOCK_BUTTON}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setReportModalOpen(true)}
                  className="gap-5 py-3 rounded-xl hover:cursor-pointer"
                  disabled={reportUserMutation.isPending}
                >
                  <CircleAlertIcon size={16} />
                  <span>{t.PROFILE_VIEW.LABELS.REPORT_BUTTON}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4">
          <PhotosCarousel
            userPhotos={data.photos}
            userId={userId!}
            privatePhotoStatus={data.private_photo_status}
          />
          <ProfileViewDetails userId={userId!} profileView={data} />
        </div>
      </div>

      <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
        <DialogContent className="dark">
          <DialogTitle className="text-white flex flex-row items-center">
            {t.PROFILE_VIEW.LABELS.REPORT_DIALOG_TITLE}
          </DialogTitle>
          <DialogDescription>
            {t.PROFILE_VIEW.LABELS.REPORT_DIALOG_DESCRIPTION}
          </DialogDescription>
          <Form {...reportForm}>
            <form
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={reportForm.handleSubmit(handleReportUser)}
              className="space-y-4 text-white"
            >
              <FormField
                control={reportForm.control}
                name="reportReason"
                render={({ field }) => (
                  <FormItem className="">
                    <FormControl>
                      <Textarea
                        className="resize-none"
                        placeholder="Min 7 characters"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="mt-1" />
                  </FormItem>
                )}
              />
              <div className="mt-4 w-full flex flex-row justify-between">
                <Button
                  variant="destructive"
                  type="submit"
                  disabled={reportUserMutation.isPending}
                >
                  {reportUserMutation.isPending ? (
                    <SpinnerSmall />
                  ) : (
                    t.PROFILE_VIEW.LABELS.REPORT_USER_BUTTON
                  )}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setReportModalOpen(false);
                    reportForm.reset();
                  }}
                >
                  {t.SHARED.LABELS.CANCEL_BUTTON}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
